import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Alert, Button} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import StyledCard from "components/card/StyledCard";
import BeatLoaderRegular from "components/loaders/BeatLoaderRegular";

import {black500, greyishBlue100, greyishBlue600, orange600} from "assets/jss/main";

import useConversation from "views/personalShopper/hooks/useConversation";
import {EscalationChips, HolderChip, WindowChip} from "views/personalShopper/components/StatusChips";
import StatusChip from "views/personalShopper/components/StatusChip";
import {formatRelative} from "views/personalShopper/components/formatters";

import CustomerSheetPane from "./CustomerSheetPane";
import HeaderActions from "./components/HeaderActions";
import SuspensionPanel from "./components/SuspensionPanel";
import Transcript from "./components/Transcript";
import ReplyBox from "./components/ReplyBox";

ConversationPane.propTypes = {
    id: PropTypes.number.isRequired,
    memberEmail: PropTypes.string,
    narrow: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    onSummaryChange: PropTypes.func.isRequired,
    onErased: PropTypes.func.isRequired,
};

/**
 * The centre column (the thread, kept live, and the reply) and the right one (the customer
 * sheet). Two grid cells, so the workspace lays them out. Every change of the conversation
 * is handed back to the workspace so the list follows.
 */
function ConversationPane({id, memberEmail, narrow, onBack, onSummaryChange, onErased}) {
    const live = useConversation(id);
    const {conversation, messages, loading, error, pollingBroken} = live;

    useEffect(() => {
        if (conversation) {
            onSummaryChange(conversation);
        }
    }, [conversation, onSummaryChange]);

    const heldByMe = !!conversation?.handledBy && conversation.handledBy === memberEmail;

    return (
        <>
            <StyledCard noHoverShadow style={{padding: 0, display: "flex", flexDirection: "column", minWidth: 0}}>
                {narrow && (
                    <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{textTransform: "none", alignSelf: "flex-start", margin: "8px"}}>
                        <FormattedMessage id="personalShopper.conversation.back" />
                    </Button>
                )}
                {loading && !conversation && <div style={{padding: "24px", textAlign: "center"}}><BeatLoaderRegular loading /></div>}
                {error && (
                    <Alert severity="error" sx={{margin: "12px"}}>
                        {error.status === 404 ? <FormattedMessage id="personalShopper.conversation.notFound" /> : error.message}
                    </Alert>
                )}
                {conversation && (
                    <>
                        <div style={{padding: "12px 16px", borderBottom: `1px solid ${greyishBlue100}`}}>
                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap"}}>
                                <div style={{minWidth: 0}}>
                                    <div style={{fontSize: "18px", fontWeight: 700, color: black500}}>
                                        {conversation.profileName || <FormattedMessage id="personalShopper.customer.anonymous" />}
                                        <span style={{color: greyishBlue600, fontWeight: 400, fontSize: "14px", marginLeft: "10px"}}>{conversation.phone}</span>
                                    </div>
                                    <div style={{display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px", alignItems: "center"}}>
                                        <StatusChip status={conversation.status} />
                                        <HolderChip handledBy={conversation.handledBy} memberEmail={memberEmail} />
                                        <WindowChip open={conversation.customerServiceWindowOpen} />
                                        <EscalationChips categories={conversation.openEscalations} />
                                        {conversation.awaitingHuman && (
                                            <span style={{color: orange600, fontSize: "12px", fontWeight: 600}}>
                                                <FormattedMessage id="personalShopper.list.awaitingSince" values={{since: formatRelative(conversation.lastInboundAt)}} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <HeaderActions conversation={conversation} heldByMe={heldByMe} onTakeOver={live.takeOver} onRelease={live.release} onSendProducts={live.sendProducts} />
                            </div>
                        </div>
                        <div style={{padding: "12px 16px 0"}}>
                            {pollingBroken && (
                                <Alert severity="warning" sx={{marginBottom: "12px"}}>
                                    <FormattedMessage id="personalShopper.conversation.pollingBroken" />
                                </Alert>
                            )}
                            <SuspensionPanel conversation={conversation} onSuspend={live.suspend} onReactivate={live.reactivate} />
                        </div>
                        <div style={{padding: "0 16px 16px"}}>
                            <Transcript messages={messages} shopperName={conversation.profileName} />
                            <ReplyBox conversation={conversation} heldByMe={heldByMe} onReply={live.reply} />
                        </div>
                    </>
                )}
            </StyledCard>
            {conversation
                ? <CustomerSheetPane conversation={conversation} messages={messages} live={live} onErased={onErased} />
                : <div />}
        </>
    );
}

export default ConversationPane;
